import type { SeasonDeepContent } from "./types";

export const springDeepContent: SeasonDeepContent = {
  LIGHT: {
    intro: `Light Spring sits at the pale, warm end of the color wheel — the softest and most delicate of the three Spring subseasons. Undertone is warm and golden, never rosy or olive. Value runs light: skin, hair, and eyes cluster in the pale-to-medium range with little contrast between them, which is what makes this coloring look washed out under heavy or dark color. Chroma leans clear rather than muted, so the palette isn't dusty like a Summer's — it's soft pastels with a touch of shine, like apricot, buttercream, and mint, rather than flat grey-toned hues. Picture the light spring color palette as Light Summer's undertone flipped from cool to warm, while staying just as gentle and low-contrast. Move toward more saturation and depth and you land in True Spring; push the same warmth toward higher contrast and vivid color and you're in Bright Spring territory. Light Spring is the hinge between Spring's warmth and Summer's softness — warm enough to glow in peach, delicate enough to be overwhelmed by anything too saturated or too dark.`,
    selfCheck: {
      skin: [
        "Fair to light skin with a peachy or golden cast, never pink or olive",
        "Flushes or tans to a soft golden-peach rather than red or bronze",
        "Looks washed out under stark white or heavy, dark makeup",
        "Warms up noticeably next to cream and ivory compared to bright white",
      ],
      hair: [
        "Natural color in the light range: golden blonde, strawberry blonde, or light golden brown",
        "Rarely naturally black, jet dark, or ash-toned",
        "Childhood hair color was often lighter and warmer than current shade",
        "Looks best with soft, warm highlights rather than cool balayage",
      ],
      eyes: [
        "Light, clear eye colors: blue, green, aqua, or light hazel",
        "Often flecked with gold or warm amber near the pupil",
        "Rarely deep brown or black-brown",
        "Eyes look brighter next to warm pastels than next to jewel tones",
      ],
      overall: [
        "Low to medium contrast between skin, hair, and eyes",
        "Gold jewelry flatters more than silver",
        "Pastel, warm-toned clothing reads as healthy and glowing, not sickly",
        "Bold, saturated, or very dark colors overpower rather than flatter",
      ],
    },
    avoidDetails: [
      { name: "Black", hex: "#000000", reason: "Too heavy and stark for Light Spring's low-contrast, delicate coloring — it drains the face instead of framing it.", swapFor: "Soft Camel", swapHex: "#D2B48C" },
      { name: "Deep Navy", hex: "#000080", reason: "Reads as cool and dense next to warm, pale skin, dulling the natural peachy glow.", swapFor: "Light Periwinkle", swapHex: "#D6C9FF" },
      { name: "Charcoal", hex: "#36454F", reason: "A cool, dark neutral that fights Light Spring's warmth and washes out light eyes.", swapFor: "Pale Taupe", swapHex: "#BC987E" },
      { name: "Burgundy", hex: "#800020", reason: "Too deep and cool-leaning; it overwhelms the face rather than lighting it up.", swapFor: "Light Coral", swapHex: "#F88379" },
      { name: "Icy Pink", hex: "#FFDDF4", reason: "The blue-based coolness clashes with Light Spring's golden undertone and can look chalky.", swapFor: "Blush Pink", swapHex: "#FADADD" },
      { name: "Stark White", hex: "#FFFFFF", reason: "Pure white has too much contrast and coolness for this soft palette; it competes with the skin instead of complementing it.", swapFor: "Cream", swapHex: "#FFFDD0" },
    ],
    comparisons: [
      {
        vsLabel: "True Spring",
        vsHref: "/color-palette/spring/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, soft golden", otherSeason: "Warm, clear golden" },
          { attribute: "Depth", thisSeason: "Light, low contrast", otherSeason: "Medium, moderate contrast" },
          { attribute: "Chroma", thisSeason: "Soft pastel with light clarity", otherSeason: "Fully clear and saturated" },
          { attribute: "Best pink", thisSeason: "Blush Pink", otherSeason: "Warm Watermelon", thisHex: "#FADADD", otherHex: "#FF5C7A" },
          { attribute: "Metals", thisSeason: "Soft yellow gold", otherSeason: "Bright yellow gold", thisHex: "#FFD700", otherHex: "#FFD700" },
        ],
      },
      {
        vsLabel: "Light Summer",
        vsHref: "/color-palette/summer/light",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, peachy", otherSeason: "Cool, pink-based" },
          { attribute: "Depth", thisSeason: "Light, low contrast", otherSeason: "Light, low contrast" },
          { attribute: "Chroma", thisSeason: "Soft with warm clarity", otherSeason: "Soft and muted" },
          { attribute: "Best blue", thisSeason: "Light Aqua", otherSeason: "Sky Blue (cool)", thisHex: "#B3E3E2", otherHex: "#87CEEB" },
          { attribute: "Metals", thisSeason: "Gold", otherSeason: "Silver", thisHex: "#FFD700", otherHex: "#C0C0C0" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Soft Camel", hex: "#D2B48C" },
      { item: "Blazer", color: "Pale Gold", hex: "#F7E7CE" },
      { item: "Blouse", color: "Buttercream", hex: "#FFF1C1" },
      { item: "Trousers", color: "Warm Beige", hex: "#F5F5DC" },
      { item: "Sweater", color: "Mint Green", hex: "#C4E1C1" },
      { item: "Dress", color: "Light Coral", hex: "#F88379" },
      { item: "Cardigan", color: "Apricot", hex: "#FBCEB1" },
      { item: "Shoes", color: "Cream", hex: "#FFFDD0" },
      { item: "Bag", color: "Pale Taupe", hex: "#BC987E" },
      { item: "Denim", color: "Light Periwinkle", hex: "#D6C9FF" },
    ],
    celebrities: [
      {
        name: "Amanda Seyfried",
        why: "Light golden-blonde hair, pale warm skin, and clear blue-green eyes give her the low-contrast, luminous coloring that defines Light Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/Amanda_Seyfried_at_Berlinale_2026-1_%28cropped%29.jpg/330px-Amanda_Seyfried_at_Berlinale_2026-1_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Amanda_Seyfried",
      },
      {
        name: "Dakota Fanning",
        why: "Pale skin with warm undertones and light blonde hair create the gentle, delicate palette Light Spring is built around.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Dakota_Fanning_SAG_AWARDS_2020.jpg/330px-Dakota_Fanning_SAG_AWARDS_2020.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Dakota_Fanning",
      },
      {
        name: "Elle Fanning",
        why: "Light, warm-toned hair and fair skin with soft coloring make her a textbook example of Light Spring's pastel-friendly look.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Elle_Fanning_by_Gage_Skidmore_2.jpg/330px-Elle_Fanning_by_Gage_Skidmore_2.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Elle_Fanning",
      },
      {
        name: "Rachel McAdams",
        why: "Warm, light-to-medium blonde hair and fair skin with a peachy cast give her the soft warmth typical of Light Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Rachel_McAdams_-_Walk_of_Fame.jpg/330px-Rachel_McAdams_-_Walk_of_Fame.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Rachel_McAdams",
      },
      {
        name: "Sienna Miller",
        why: "Fair, warm-peachy skin and light golden-blonde hair give her the soft, low-contrast glow characteristic of Light Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/06/Sienna_Miller_Berlinale_2017.jpg/330px-Sienna_Miller_Berlinale_2017.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Sienna_Miller",
      },
    ],
    extraFaqs: [
      {
        question: "How is Light Spring different from True Spring?",
        answer: "Light Spring is softer and paler with lower contrast; True Spring is warm and clear but can carry more saturation and medium depth. If bright coral looks like too much next to your face but a pastel peach feels right, you're Light Spring.",
      },
      {
        question: "How is Light Spring different from Light Summer?",
        answer: "Both are pale and low-contrast, but the undertone flips: Light Spring is warm and peachy, Light Summer is cool and pink-based. Hold a peach fabric and a cool pink fabric near your face — Light Springs glow with the peach.",
      },
      {
        question: "What jewelry metals suit Light Spring?",
        answer: "Soft yellow gold and rose gold are most flattering. Avoid heavy silver or oxidized finishes, which read as cool and can make warm, light skin look dull. Delicate, thin pieces suit this subseason better than chunky statement jewelry.",
      },
      {
        question: "What's the best hair color for Light Spring?",
        answer: "Golden blonde, strawberry blonde, and light golden brown are ideal. Avoid ash tones or anything too dark and cool — they fight the natural warmth and lightness that make Light Spring coloring look healthy and bright.",
      },
      {
        question: "How can I confirm I'm a Light Spring?",
        answer: "Drape a warm pastel like peach or buttercream against your face, then try a deep, cool color like burgundy. If the pastel makes your skin look brighter and the deep color makes you look tired or older, that's a strong Light Spring signal.",
      },
    ],
    vibe: {
      skinTones: ["#FDE6D8", "#F5D3B8", "#E8B894"],
      hairTones: ["#E8D4A2", "#D4A84B", "#D4956A", "#C67C4E"],
      eyeColors: ["#A8D8E8", "#8FBF8A", "#C9E4A0", "#D4B896"],
    },
    hairToneSwatches: [
      { name: "Golden Blonde", hexes: ["#AE9F7A", "#E8D4A2", "#EEDFB9"] },
      { name: "Warm Honey", hexes: ["#9F7E38", "#D4A84B", "#DFBE78"] },
      { name: "Strawberry Blonde", hexes: ["#9F7050", "#D4956A", "#DFB08F"] },
      { name: "Light Copper", hexes: ["#945D3A", "#C67C4E", "#D49D7A"] },
      { name: "Light Warm Brown", hexes: ["#7C5C44", "#A67B5B", "#BC9C84"] },
      { name: "Caramel", hexes: ["#946A44", "#C68E5A", "#D4AA83"] },
      { name: "Soft Ginger", hexes: ["#936238", "#C4824A", "#D3A177"] },
    ],
    metals: [
      { name: "Light Gold", hexes: ["#BFA100", "#FFD700", "#FFE140"] },
      { name: "Rose Gold", hexes: ["#AE878A", "#E8B4B8", "#EEC7CA"] },
      { name: "Warm Brass", hexes: ["#9A7058", "#CD9575", "#DAB098"] },
    ],
  },
  TRUE: {
    intro: `True Spring is the "quintessential" Spring — the subseason where warmth and clarity are both at their most balanced. Undertone is unmistakably warm and golden, more obviously so than Light Spring's soft peach. Value sits in the light-to-medium range, deeper and richer than Light Spring but nowhere near Autumn's depth. Chroma is clear and bright rather than muted: colors read as fresh and saturated, not dusty or earthy, which is the main thing that separates True Spring from its warm-and-muted cousin, True Autumn. Think of the true spring color palette as sitting between Light Spring's pastel softness and Bright Spring's high-voltage contrast — warm like Autumn, but clear like Winter, with none of either season's extremes. Coral, golden yellow, and warm turquoise all look natural rather than costume-like on True Spring. The tell is simple: if a color looks "clean" and "alive" on you rather than "dusty" or "faded," and it's warm rather than cool, you're likely in True Spring territory.`,
    selfCheck: {
      skin: [
        "Obvious golden or peachy warmth, medium depth",
        "Tans easily and looks healthy with some sun exposure",
        "Looks sallow or grey in cool, ashy colors",
        "Comes alive next to clear, warm colors like coral or golden yellow",
      ],
      hair: [
        "Natural color has visible golden, copper, or warm auburn tones",
        "Medium depth — not as light as Light Spring, not as deep as Bright Spring often runs",
        "Highlights should be warm (honey, caramel) rather than ashy",
        "Looks flat or brassy-in-the-wrong-way with cool, neutral browns",
      ],
      eyes: [
        "Warm hazel, green, amber, or clear light-to-medium brown",
        "Often has visible gold or warm flecks",
        "Eyes look most vivid next to clear warm colors, not muted ones",
        "Rarely a cool, flat grey or blue-grey",
      ],
      overall: [
        "Medium, moderate contrast between skin, hair, and eyes",
        "Gold jewelry is clearly more flattering than silver",
        "Muted or dusty colors (sage, mauve) make the whole look dull",
        "Clear, saturated warm colors read as natural rather than costumey",
      ],
    },
    avoidDetails: [
      { name: "Dull Olive", hex: "#808000", reason: "Too muted and flat for True Spring's need for clarity, making the complexion look tired.", swapFor: "Jade", swapHex: "#00A86B" },
      { name: "Muddy Brown", hex: "#8B4513", reason: "Lacks the brightness True Spring needs; it reads as dingy rather than warm.", swapFor: "Amber", swapHex: "#FFBF00" },
      { name: "Dusty Rose", hex: "#C08081", reason: "The muted, cool-leaning finish fights True Spring's clear warm undertone.", swapFor: "Warm Watermelon", swapHex: "#FF5C7A" },
      { name: "Charcoal Grey", hex: "#36454F", reason: "A cool, dense neutral that dulls True Spring's natural vibrancy.", swapFor: "Light Camel", swapHex: "#D2B48C" },
      { name: "Burgundy", hex: "#800020", reason: "Too deep and blue-based; it overwhelms True Spring's medium, warm coloring.", swapFor: "Coral", swapHex: "#FF6F61" },
      { name: "Icy Lavender", hex: "#E6E6FA", reason: "Too cool and pale to register against True Spring's warmth — it washes out rather than flatters.", swapFor: "Bright Lavender", swapHex: "#BF94E4" },
    ],
    comparisons: [
      {
        vsLabel: "Light Spring",
        vsHref: "/color-palette/spring/light",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, clear golden", otherSeason: "Warm, soft golden" },
          { attribute: "Depth", thisSeason: "Medium, moderate contrast", otherSeason: "Light, low contrast" },
          { attribute: "Chroma", thisSeason: "Fully clear and saturated", otherSeason: "Soft pastel with light clarity" },
          { attribute: "Best blue/red", thisSeason: "Sapphire / Warm Poppy", otherSeason: "Light Aqua / Light Coral", thisHex: "#0F52BA", otherHex: "#B3E3E2" },
          { attribute: "Metals", thisSeason: "Bright yellow gold", otherSeason: "Soft yellow gold", thisHex: "#FFD700", otherHex: "#FFD700" },
        ],
      },
      {
        vsLabel: "Bright Spring",
        vsHref: "/color-palette/spring/bright",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, clear golden", otherSeason: "Warm, clear golden" },
          { attribute: "Depth", thisSeason: "Medium, moderate contrast", otherSeason: "Medium, high contrast" },
          { attribute: "Chroma", thisSeason: "Clear and saturated", otherSeason: "Maximum saturation and vividness" },
          { attribute: "Best pink", thisSeason: "Warm Watermelon", otherSeason: "Warm Pink (neon-adjacent)", thisHex: "#FF5C7A", otherHex: "#FF69B4" },
          { attribute: "Metals", thisSeason: "Yellow gold, copper", otherSeason: "Bright, polished gold", thisHex: "#FFD700", otherHex: "#FFD700" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Light Camel", hex: "#D2B48C" },
      { item: "Blazer", color: "Sand", hex: "#F4E1C1" },
      { item: "Blouse", color: "Warm Ivory", hex: "#FFF8E1" },
      { item: "Trousers", color: "Soft Beige", hex: "#F5F5DC" },
      { item: "Sweater", color: "Jade", hex: "#00A86B" },
      { item: "Dress", color: "Coral", hex: "#FF6F61" },
      { item: "Cardigan", color: "Amber", hex: "#FFBF00" },
      { item: "Shoes", color: "Sand", hex: "#F4E1C1" },
      { item: "Bag", color: "Golden", hex: "#FFD700" },
      { item: "Denim", color: "Azure", hex: "#007FFF" },
    ],
    celebrities: [
      {
        name: "Cameron Diaz",
        why: "Warm golden-blonde hair and clear, medium-warm skin give her the fresh, saturated coloring typical of True Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Cameron_Diaz_at_WWD_Style_Awards_2026-1998.jpg/330px-Cameron_Diaz_at_WWD_Style_Awards_2026-1998.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Cameron_Diaz",
      },
      {
        name: "Nicole Kidman",
        why: "Warm copper-red hair and fair, warm skin with clear undertones place her squarely in True Spring's bright, golden-warm range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/MKr384262_Nicole_Kidman_%28Women_In_Motion%2C_Cannes_2025%29.jpg/330px-MKr384262_Nicole_Kidman_%28Women_In_Motion%2C_Cannes_2025%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Nicole_Kidman",
      },
      {
        name: "Isla Fisher",
        why: "Vivid copper hair and warm, clear skin with bright eyes are a strong match for True Spring's warm-and-vivid balance.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/Isla_Fisher_2013.jpg/330px-Isla_Fisher_2013.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Isla_Fisher",
      },
      {
        name: "Heidi Klum",
        why: "Warm golden-blonde hair and clear, sun-kissed skin give her the medium-depth warmth and clarity that define True Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/07/Heidi_Klum_by_Glenn_Francis.jpg/330px-Heidi_Klum_by_Glenn_Francis.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Heidi_Klum",
      },
      {
        name: "Kate Hudson",
        why: "Warm blonde hair with golden depth and clear, glowing skin reflect True Spring's fresh, saturated warmth.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/01/Kate_Hudson_-_Song_Sung_Blue.jpg/330px-Kate_Hudson_-_Song_Sung_Blue.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Kate_Hudson",
      },
      {
        name: "Amy Poehler",
        why: "Warm, clear medium-blonde hair and golden-warm skin tone put her in True Spring's balanced warm-and-bright category.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/df/Amy_Poehler_%2853671989427%29_cropped.jpg/330px-Amy_Poehler_%2853671989427%29_cropped.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Amy_Poehler",
      },
    ],
    extraFaqs: [
      {
        question: "Can True Spring wear black?",
        answer: "Black is generally too harsh and cool for True Spring's warm, clear coloring — it can make the skin look sallow. Warm dark brown or deep olive work better as your darkest neutral, especially kept away from the face.",
      },
      {
        question: "How is True Spring different from Light Spring?",
        answer: "True Spring has more depth and saturation; Light Spring is paler and softer with lower contrast. If bright, clear coral feels natural on you rather than overwhelming, you're likely True Spring rather than Light Spring.",
      },
      {
        question: "How is True Spring different from Bright Spring?",
        answer: "Both are warm and clear, but Bright Spring has more contrast and can handle even more saturation. True Spring is the balanced middle — warm and clear without the extra intensity. If maximum-vivid colors feel like too much, you're True Spring.",
      },
      {
        question: "What jewelry metals suit True Spring?",
        answer: "Yellow gold, brass, and copper are the strongest choices, with rose gold as a solid option. Silver and platinum tend to look disconnected from True Spring's warm undertone, making jewelry look slightly off rather than polished.",
      },
      {
        question: "What's the best hair color for True Spring?",
        answer: "Warm copper, golden auburn, and rich golden brown all work well. The goal is warmth with clarity — avoid ash, cool neutral tones, or anything that mutes the natural brightness of this coloring.",
      },
      {
        question: "How can I confirm I'm a True Spring?",
        answer: "Compare a clear, bright turquoise to a muted olive green near your face. True Springs light up next to the turquoise; True Autumns look better in the olive. If the clear, saturated color wins, True Spring is a strong match.",
      },
    ],
    vibe: {
      skinTones: ["#FCE0C8", "#EFC49C", "#D9A26E", "#C48A56"],
      hairTones: ["#996515", "#B87333", "#A52A2A", "#DAA520"],
      eyeColors: ["#8A9A5B", "#B08040", "#5C8A5C", "#C9A227"],
    },
    hairToneSwatches: [
      { name: "Warm Golden Brown", hexes: ["#734C10", "#996515", "#B28C50"] },
      { name: "Copper", hexes: ["#8A5626", "#B87333", "#CA9666"] },
      { name: "Warm Auburn", hexes: ["#7C2020", "#A52A2A", "#BC5F5F"] },
      { name: "Golden Blonde", hexes: ["#A47C18", "#DAA520", "#E3BC58"] },
      { name: "Warm Red", hexes: ["#89310A", "#B7410E", "#C9704A"] },
      { name: "Honey Brown", hexes: ["#884C16", "#B5651D", "#C88C56"] },
      { name: "Caramel", hexes: ["#946A44", "#C68E5A", "#D4AA83"] },
    ],
    metals: [
      { name: "Yellow Gold", hexes: ["#BFA100", "#FFD700", "#FFE140"] },
      { name: "Brass", hexes: ["#887C32", "#B5A642", "#C8BC71"] },
      { name: "Copper", hexes: ["#8A5626", "#B87333", "#CA9666"] },
    ],
  },
  BRIGHT: {
    intro: `Bright Spring is the most vivid of the three Spring subseasons — warm undertone, pushed to the highest chroma and contrast the Spring family allows. Undertone stays warm and golden, same family as Light and True Spring, but contrast runs higher: hair, skin, and eyes show more separation than in softer Springs. Chroma is where Bright Spring stands out — colors need to be clear and saturated, almost electric, or they read as dull. This is the bright spring color palette that borrows Winter's intensity while keeping Spring's warmth, which is why it's most often confused with Bright Winter. The dividing line is undertone: Bright Spring wants warm coral and golden-based brights, while Bright Winter wants cool fuchsia and blue-based brights. Move down in contrast and saturation from Bright Spring and you land in True Spring; keep the brightness but flip the undertone cool and you're in Cool Winter instead. Muted or dusty colors are the fastest way to look tired here — Bright Spring needs real voltage.`,
    selfCheck: {
      skin: [
        "Warm undertone with noticeable contrast against hair and eyes",
        "Looks most alive next to vivid, saturated color, not muted tones",
        "Can appear dull or washed out in soft pastels or dusty neutrals",
        "Tans well and often has a naturally warm, lit-from-within quality",
      ],
      hair: [
        "Often medium-to-deep with clear warmth: bright copper, auburn, or golden brown",
        "High contrast against skin is common, especially with dark or vivid hair",
        "Ashy, muted, or mousy tones make the hair look flat rather than rich",
        "Shiny, glossy finishes suit this hair better than matte ones",
      ],
      eyes: [
        "Bright, clear eye color — vivid green, warm hazel, or clear amber-brown",
        "High contrast between iris and white of the eye",
        "Eyes look most vivid next to saturated warm colors",
        "Muted eyeshadow shades tend to flatten rather than define the eyes",
      ],
      overall: [
        "High contrast between skin, hair, and eyes compared to other Springs",
        "Vivid, saturated colors look natural; muted colors look dull",
        "Polished, shiny gold jewelry outperforms matte or antique finishes",
        "Neon and jewel-bright warm colors read as flattering, not costumey",
      ],
    },
    avoidDetails: [
      { name: "Muted Olive", hex: "#808000", reason: "Too flat and low-chroma for Bright Spring, which needs vividness to look alive.", swapFor: "Vivid Grass Green", swapHex: "#7FFF00" },
      { name: "Muddy Brown", hex: "#8B4513", reason: "Lacks the clarity Bright Spring's high-contrast coloring demands.", swapFor: "Clear Orange", swapHex: "#FF9900" },
      { name: "Dull Purple", hex: "#8B668B", reason: "Too muted and cool-leaning; it flattens Bright Spring's natural vividness.", swapFor: "Bright Lilac", swapHex: "#C8A2C8" },
      { name: "Dark Grey", hex: "#2F4F4F", reason: "A cool, muted neutral that dulls Bright Spring's warm, high-voltage coloring.", swapFor: "Warm White", swapHex: "#FFF8E7" },
      { name: "Dusty Rose", hex: "#C08081", reason: "Too soft and muted to register against Bright Spring's high contrast.", swapFor: "Neon Coral", swapHex: "#FF5E5B" },
      { name: "Pastel Pink", hex: "#FFD1DC", reason: "Too pale and low-chroma; Bright Spring needs saturation, not softness.", swapFor: "Warm Pink", swapHex: "#FF69B4" },
    ],
    comparisons: [
      {
        vsLabel: "True Spring",
        vsHref: "/color-palette/spring/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, clear golden", otherSeason: "Warm, clear golden" },
          { attribute: "Depth", thisSeason: "Medium, high contrast", otherSeason: "Medium, moderate contrast" },
          { attribute: "Chroma", thisSeason: "Maximum saturation and vividness", otherSeason: "Clear and saturated" },
          { attribute: "Best pink", thisSeason: "Warm Pink (neon-adjacent)", otherSeason: "Warm Watermelon", thisHex: "#FF69B4", otherHex: "#FF5C7A" },
          { attribute: "Metals", thisSeason: "Bright, polished gold", otherSeason: "Yellow gold, copper", thisHex: "#FFD700", otherHex: "#FFD700" },
        ],
      },
      {
        vsLabel: "Cool Winter",
        vsHref: "/color-palette/winter/cool",
        rows: [
          { attribute: "Undertone", thisSeason: "Warm, golden", otherSeason: "Cool, blue-based" },
          { attribute: "Depth", thisSeason: "Medium, high contrast", otherSeason: "Medium to deep, high contrast" },
          { attribute: "Chroma", thisSeason: "Vivid and warm-clear", otherSeason: "Vivid and cool-clear" },
          { attribute: "Best red", thisSeason: "Hot Tomato (orange-red)", otherSeason: "True Red (blue-red)", thisHex: "#FF6347", otherHex: "#C8102E" },
          { attribute: "Metals", thisSeason: "Bright yellow gold", otherSeason: "Silver, platinum", thisHex: "#FFD700", otherHex: "#C0C0C0" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Warm White", hex: "#FFF8E7" },
      { item: "Blazer", color: "Light Tan", hex: "#D2B48C" },
      { item: "Blouse", color: "Bright Lemon", hex: "#FFF700" },
      { item: "Trousers", color: "Soft Camel", hex: "#C19A6B" },
      { item: "Sweater", color: "Turquoise Blue", hex: "#00CED1" },
      { item: "Dress", color: "Neon Coral", hex: "#FF5E5B" },
      { item: "Cardigan", color: "Clear Orange", hex: "#FF9900" },
      { item: "Shoes", color: "Buttermilk", hex: "#FFF1C1" },
      { item: "Bag", color: "Goldenrod", hex: "#DAA520" },
      { item: "Denim", color: "Bright Aqua", hex: "#00FFFF" },
    ],
    celebrities: [
      {
        name: "Reese Witherspoon",
        why: "High contrast between warm blonde hair, fair skin, and bright eyes gives her the vivid, energetic coloring typical of Bright Spring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Reese_Witherspoon_%2829144489784%29_%28cropped%29.jpg/330px-Reese_Witherspoon_%2829144489784%29_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Reese_Witherspoon",
      },
      {
        name: "Blake Lively",
        why: "Warm, bright blonde hair against clear, contrasted skin and eyes reflects Bright Spring's need for high-voltage color.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/Guests_at_the_2026_Met_Gala_435_%28Blake_Lively%29.jpg/330px-Guests_at_the_2026_Met_Gala_435_%28Blake_Lively%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Blake_Lively",
      },
      {
        name: "Lupita Nyong'o",
        why: "Deep, rich skin with warm undertones and high contrast against bright eyes make her a strong Bright Spring example — proof this subseason isn't limited to fair skin.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c4/LupitaNyongo-byPhilipRomano3.jpg/330px-LupitaNyongo-byPhilipRomano3.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Lupita_Nyong%27o",
      },
      {
        name: "Emma Stone",
        why: "Warm reddish-blonde hair with clear, high-contrast skin and vivid eyes give her the bright, warm intensity that defines this subseason.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Emma_Stone_at_the_2025_Venice_Film_Festival-6313_%28cropped%29.jpg/330px-Emma_Stone_at_the_2025_Venice_Film_Festival-6313_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Emma_Stone",
      },
      {
        name: "Dianna Agron",
        why: "Warm blonde hair with clear, bright skin and high contrast against her eyes place her in Bright Spring's vivid, high-energy range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1c/Dianna_Agron_%2838573320982%29_%28cropped%29.jpg/330px-Dianna_Agron_%2838573320982%29_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Dianna_Agron",
      },
    ],
    extraFaqs: [
      {
        question: "Can Bright Spring wear black?",
        answer: "Black alone is usually too flat and cool for Bright Spring, but it can work paired with a vivid warm accent near the face, like coral or turquoise. On its own, warm charcoal or deep golden brown is a safer dark neutral.",
      },
      {
        question: "How is Bright Spring different from True Spring?",
        answer: "Bright Spring has more contrast and can wear more saturated, higher-voltage color. True Spring is the balanced warm-and-clear middle ground. If maximum-vivid colors feel natural rather than costumey, you're likely Bright Spring.",
      },
      {
        question: "How is Bright Spring different from Cool Winter?",
        answer: "Both wear vivid, high-contrast color well, but the undertone flips: Bright Spring is warm (coral, orange-red), Cool Winter is cool (fuchsia, blue-red). Try a warm coral against a cool fuchsia — Bright Springs glow in the coral.",
      },
      {
        question: "What jewelry metals suit Bright Spring?",
        answer: "Bright, polished yellow gold is the strongest choice — the shine matches this subseason's need for clarity and energy. Avoid matte, antiqued, or brushed finishes, which mute the vividness that makes Bright Spring coloring work.",
      },
      {
        question: "What hair colors should Bright Spring avoid?",
        answer: "Ashy, mousy, or muted browns tend to flatten Bright Spring's natural vibrancy. Rich copper, bright auburn, and clear golden tones keep the warmth and clarity this coloring needs to look its best.",
      },
      {
        question: "How can I confirm I'm a Bright Spring?",
        answer: "Hold a vivid warm coral and a muted dusty rose near your face. If the coral makes you look brighter and more awake while the muted color falls flat, that's a strong sign you're Bright Spring rather than a softer Spring subseason.",
      },
    ],
    vibe: {
      skinTones: ["#FBE2CC", "#F0C8A0", "#D8A472", "#BE8858"],
      hairTones: ["#DA8A67", "#E0C068", "#B55239", "#C65D3B"],
      eyeColors: ["#4A9A6E", "#C9A227", "#3D8FA0", "#8A5A2E"],
    },
    hairToneSwatches: [
      { name: "Bright Copper", hexes: ["#A4684D", "#DA8A67", "#E3A78D"] },
      { name: "Warm Golden Blonde", hexes: ["#A8904E", "#E0C068", "#E8D08E"] },
      { name: "Clear Auburn", hexes: ["#883E2B", "#B55239", "#C87D6A"] },
      { name: "Bright Ginger", hexes: ["#94462C", "#C65D3B", "#D4866C"] },
      { name: "Warm Chestnut", hexes: ["#68340E", "#8B4513", "#A8744E"] },
      { name: "Bright Caramel", hexes: ["#9E4F16", "#D2691E", "#DD8E56"] },
      { name: "Rich Copper", hexes: ["#783E22", "#A0522D", "#B87D62"] },
    ],
    metals: [
      { name: "Bright Gold", hexes: ["#BFA100", "#FFD700", "#FFE140"] },
      { name: "Rose Gold", hexes: ["#AE878A", "#E8B4B8", "#EEC7CA"] },
      { name: "Polished Brass", hexes: ["#9B882C", "#CFB53B", "#DBC86C"] },
    ],
  },
};
